import React, { useContext, useMemo, useState } from "react";
import format from "date-fns/format";
import axios from "axios";
import AuthContext from "../../store/AuthContext";
import PortalContext from "../../store/PortalContext";
import PortalQuoteFeedbackDrawer from "./PortalQuoteFeedbackDrawer";
import chatIcon from "../../assets/icons/chat-gold.svg";
import { resolveBrokerAction } from "../../services/brokerActions";
import { useCommercialData } from "../../store/CommercialDataContext";
import envVariables from "../../data/envVariables";
import LoadingOverlay from "../shared/LoadingOverlay";
import SelectDropdown from "../ui/SelectDropdown";
import personLight from "../../assets/icons/person-light.svg";
import brokerIcon from "../../assets/icons/broker.svg";
import stageFilterIcon from "../../assets/icons/stage-filter.svg";
import { commercialRepOptions, portalFilterOptions } from "../../data/portalFormListData";
import {
    CHECKLIST_STAGES,
    COMMERCIAL_COLUMNS,
    FEEDBACK_FILTER_OPTIONS,
    distinctBrokers,
    filterDeals,
    getStageColour,
    groupDealsByStage,
} from "../../utilities/commercialChecklist";
import { CommercialSearch, EditableCell, SortableHeaderLabel, FilterDropdown } from "./commercialTableParts";

// Route to the sales view of a deal — used for both in-app nav and new-window links.
const dealDetailsPath = (deal) => `/portal/deal-details/sales/${deal.id}`;

const FeedbackCell = ({ summary, onOpen }) => {
    if (!summary?.hasFeedback) return null;
    const unresolved = summary.unresolvedCount > 0;
    const last = summary.lastReceivedAt ? new Date(summary.lastReceivedAt) : null;
    return (
        <button
            type="button"
            className="commercial__feedback"
            onClick={onOpen}
            aria-label={unresolved ? "Open unresolved feedback" : "Open resolved feedback"}
        >
            <span
                className={`commercial__feedback-dot commercial__feedback-dot--${unresolved ? "unresolved" : "resolved"}`}
                aria-hidden="true"
            />
            {last && !Number.isNaN(last.getTime()) && (
                <span className="commercial__stack">
                    <span className="commercial__stack-item">{format(last, "dd/MM/yyyy")}</span>
                    <span className="commercial__stack-item">{format(last, "HH:mm")}</span>
                </span>
            )}
        </button>
    );
};

const DealRow = ({ deal, onSaveField, onOpenFeedback }) => (
    <tr className="commercial__row">
        {COMMERCIAL_COLUMNS.map((col) => {
            const value = col.accessor(deal);
            return (
                <td key={col.key} className={`commercial__td commercial__td--${col.key}`}>
                    {col.editable ? (
                        <EditableCell row={deal} config={col.editable} onSaveField={onSaveField} />
                    ) : col.isFeedback ? (
                        <FeedbackCell summary={value} onOpen={() => onOpenFeedback(deal)} />
                    ) : col.isDealLink ? (
                        <a
                            href={dealDetailsPath(deal)}
                            className="commercial__deal-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open deal in a new tab"
                        >
                            <span className="commercial__route">
                                {(Array.isArray(value) ? value : [value]).map((seg, i) => (
                                    <span key={i} className="commercial__route-leg">
                                        {seg}
                                    </span>
                                ))}
                            </span>
                        </a>
                    ) : col.isStacked ? (
                        <span className="commercial__stack">
                            {(Array.isArray(value) ? value : [value]).map((v, i) => (
                                <span key={i} className="commercial__stack-item">
                                    {v}
                                </span>
                            ))}
                        </span>
                    ) : col.isStatus ? (
                        <span className="commercial__status-badge" style={{ backgroundColor: getStageColour(value) }}>
                            {value}
                        </span>
                    ) : col.isAvinode && value !== "–" ? (
                        <span className="commercial__avinode">{value}</span>
                    ) : (
                        value
                    )}
                </td>
            );
        })}
        <td className="commercial__td commercial__td--spacer" />
    </tr>
);

// Single table with a heading row per group (Action Required / Awaiting Feedback / Closed).
const GroupedDealTable = ({ groups, onSaveField, onOpenFeedback, sort, onSort }) => (
    <div className="commercial__table-scroll">
        <table className="commercial__table">
            <thead>
                <tr>
                    {COMMERCIAL_COLUMNS.map((col) => (
                        <th key={col.key} className={`commercial__th commercial__th--${col.key}`}>
                            {col.sortable ? (
                                <SortableHeaderLabel col={col} sort={sort} onSort={onSort} />
                            ) : (
                                col.label
                            )}
                        </th>
                    ))}
                    <th className="commercial__th commercial__th--spacer" aria-hidden="true" />
                </tr>
            </thead>
            {groups.map((group) => (
                <tbody key={group.key}>
                    <tr className={`commercial__group-row commercial__group-row--${group.key}`}>
                        <td className="commercial__group-cell" colSpan={COMMERCIAL_COLUMNS.length + 1}>
                            {group.label}
                            <span className="commercial__group-count">{group.deals.length}</span>
                        </td>
                    </tr>
                    {group.deals.length ? (
                        group.deals.map((deal) => (
                            <DealRow key={deal.id} deal={deal} onSaveField={onSaveField} onOpenFeedback={onOpenFeedback} />
                        ))
                    ) : (
                        <tr className="commercial__row">
                            <td className="commercial__td commercial__group-empty" colSpan={COMMERCIAL_COLUMNS.length + 1}>
                                No deals in this group.
                            </td>
                        </tr>
                    )}
                </tbody>
            ))}
        </table>
    </div>
);

const PortalCommercialTable = () => {
    const [search, setSearch] = useState("");
    const [statuses, setStatuses] = useState([]);
    const [commercials, setCommercials] = useState([]);
    const [brokers, setBrokers] = useState([]);
    const [feedbackFilter, setFeedbackFilter] = useState([]);
    const [feedbackDeal, setFeedbackDeal] = useState(null);
    const portalCtx = useContext(PortalContext);
    const [sort, setSort] = useState({ key: null, dir: "asc" });

    const authCtx = useContext(AuthContext);
    const { checklistDeals, setDeals, brokerActions, setBrokerActions, source, setSource, loading } =
        useCommercialData();

    const handleSourceChange = (newSource) => {
        setStatuses([]);
        setCommercials([]);
        setBrokers([]);
        setFeedbackFilter([]);
        setSource(newSource);
    };

    const statusOptions = CHECKLIST_STAGES;
    const commercialOptions = commercialRepOptions;
    const brokerOptions = useMemo(() => distinctBrokers(checklistDeals), [checklistDeals]);

    const filtered = useMemo(
        () => filterDeals(checklistDeals, { search, statuses, commercials, brokers, feedback: feedbackFilter }),
        [checklistDeals, search, statuses, commercials, brokers, feedbackFilter]
    );

    const groups = useMemo(() => groupDealsByStage(filtered, sort), [filtered, sort]);

    const toggle = (setter) => (value) =>
        setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

    // Cycle a column: unsorted → ascending → descending → unsorted (default order).
    const onSort = (key) =>
        setSort((prev) => {
            if (prev.key !== key) return { key, dir: "asc" };
            if (prev.dir === "asc") return { key, dir: "desc" };
            return { key: null, dir: "asc" };
        });

    const feedbackNotes = useMemo(
        () => (feedbackDeal ? brokerActions.filter((a) => a.dealId === String(feedbackDeal.id)) : []),
        [brokerActions, feedbackDeal]
    );

    const handleResolve = async (id) => {
        await resolveBrokerAction(id, portalCtx?.id, authCtx?.token);
        setBrokerActions((prev) =>
            prev.map((a) =>
                a.id === id
                    ? { ...a, resolved: true, resolvedBy: portalCtx?.name || "", resolvedAt: new Date().toISOString() }
                    : a
            )
        );
    };

    const saveField = async (deal, payloadKey, value, dealKey, localValue) => {
        try {
            const headers = authCtx?.token
                ? { headers: { "Content-type": "application/json", Authorization: `Bearer ${authCtx.token}` } }
                : {};
            const res = await axios.post(
                `${envVariables.apiEndpoint}/api/portal/update-deal`,
                { dealId: deal.id, [payloadKey]: value },
                headers
            );
            if (res.status === 200 && dealKey) {
                setDeals((prev) =>
                    prev.map((d) => (d.id === deal.id ? { ...d, [dealKey]: localValue ?? value } : d))
                );
            }
            return res.status === 200;
        } catch {
            return false;
        }
    };

    return (
        <div className="commercial">
            <div className="commercial__bar">
                <div className="commercial__bar-filters">
                    <div className="commercial__sources">
                        <SelectDropdown options={portalFilterOptions} value={source} onChange={handleSourceChange} />
                    </div>
                    <FilterDropdown
                        label="Stage"
                        icon={stageFilterIcon}
                        options={statusOptions}
                        selected={statuses}
                        onToggle={toggle(setStatuses)}
                        onClear={() => setStatuses([])}
                    />
                    <FilterDropdown
                        label="Commercial Rep"
                        icon={personLight}
                        options={commercialOptions}
                        selected={commercials}
                        onToggle={toggle(setCommercials)}
                        onClear={() => setCommercials([])}
                    />
                    <FilterDropdown
                        label="Broker"
                        icon={brokerIcon}
                        options={brokerOptions}
                        selected={brokers}
                        onToggle={toggle(setBrokers)}
                        onClear={() => setBrokers([])}
                    />
                    <FilterDropdown
                        label="Feedback"
                        icon={chatIcon}
                        options={FEEDBACK_FILTER_OPTIONS}
                        selected={feedbackFilter}
                        onToggle={toggle(setFeedbackFilter)}
                        onClear={() => setFeedbackFilter([])}
                    />
                </div>

                <CommercialSearch
                    value={search}
                    onChange={setSearch}
                    placeholder="SEARCH CLIENT OR AVINODE REF"
                    ariaLabel="Search client or Avinode ref"
                />
            </div>

            {loading && <LoadingOverlay />}

            {!loading && (
                <section className="commercial__section">
                    {filtered.length ? (
                        <GroupedDealTable
                            groups={groups}
                            onSaveField={saveField}
                            onOpenFeedback={setFeedbackDeal}
                            sort={sort}
                            onSort={onSort}
                        />
                    ) : (
                        <p className="commercial__empty">No deals.</p>
                    )}
                </section>
            )}

            <PortalQuoteFeedbackDrawer
                open={!!feedbackDeal}
                onClose={() => setFeedbackDeal(null)}
                deal={feedbackDeal}
                notes={feedbackNotes}
                canResolve={!!portalCtx?.id}
                onResolve={handleResolve}
            />
        </div>
    );
};

export default PortalCommercialTable;
